/**
 * Retries an async function with exponential backoff.
 *
 * @param fn - The async function to retry
 * @param options - Configuration options
 * @param options.retries - Number of retry attempts (default: 1)
 * @param options.baseDelay - Base delay in ms before first retry (default: 100)
 * @param options.maxTime - Maximum total time in ms to keep retrying (default: 5000)
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  {
    retries = 1,
    baseDelay = 100,
    maxTime = 5000,
    onRetrySucceeded,
    onRetryFailed,
  }: {
    retries?: number;
    baseDelay?: number;
    maxTime?: number;
    onRetrySucceeded?: () => void;
    onRetryFailed?: () => void;
  } = {}
): Promise<T> {
  let lastError: unknown;
  const startTime = Date.now();
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      if (attempt > 0) {
        safeCallback(onRetrySucceeded);
      }
      return result;
    } catch (e) {
      lastError = e;
      if (attempt < retries) {
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= maxTime) {
          safeCallback(onRetryFailed);
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, baseDelay * 2 ** attempt));
      } else {
        safeCallback(onRetryFailed);
      }
    }
  }
  throw lastError;
}

// don't let a failed callback throw an error and kill the retry loop
function safeCallback(callback: undefined | (() => void)) {
  if (!callback) return;
  try {
    callback();
  } catch (e) {
    console.error(e);
  }
}
